import { useState } from 'react';
import { Button } from '@clay-run/glaze/button';
import { ComboboxOption } from '@clay-run/glaze/combobox';
import { ComboboxField } from '@clay-run/glaze/combobox-field';
import { Text } from '@clay-run/glaze/text';
import { useDebouncedValue } from '../../_shared/hooks';
import { useImportMetaAd, useMetaAdSearch, type MetaImportSource } from '../api-client';
import { jpegFile } from '../image-tools';
import { newCreative, type DraftCreative, type MetaImage } from '../draft';
import type { MetaAdImport, MetaAdSearchResult } from '../types';

const KIND_LABELS: Record<MetaAdSearchResult['kind'], string> = { campaign: 'Campaign', adset: 'Ad set', ad: 'Ad' };
// A pasted link or id goes straight to import; only names are searched.
const LOOKS_LIKE_REF = /^(https?:\/\/|\d+$)/;

const statusLabel = (status: string | null) => (status ? status.charAt(0) + status.slice(1).toLowerCase().replaceAll('_', ' ') : null);

function resultDetail(r: MetaAdSearchResult): string {
  return [KIND_LABELS[r.kind], r.campaignName && r.kind !== 'campaign' ? `in ${r.campaignName}` : null, statusLabel(r.status)]
    .filter(Boolean)
    .join(' · ');
}

async function importedMedia(dataUrl: string | null, video: MetaAdImport['creatives'][number]['video']): Promise<MetaImage | null> {
  if (!dataUrl) return null;
  const jpeg = await jpegFile(dataUrl, video ? 'thumbnail.jpg' : 'imported.jpg');
  return {
    file: jpeg.file,
    previewUrl: jpeg.dataUrl,
    width: jpeg.width,
    height: jpeg.height,
    video: video ? { file: null, url: video.sourceUrl, id: video.id } : undefined,
  };
}

/** "Start from an existing ad": pulls an ad, or a whole ad set or campaign, into the draft below. */
export function ImportAdBar({ onImport }: { onImport: (imported: MetaAdImport, creatives: DraftCreative[]) => void }) {
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<MetaAdSearchResult | null>(null);
  const [open, setOpen] = useState(false);
  const query = useDebouncedValue(text.trim(), 300);
  const searching = !LOOKS_LIKE_REF.test(query);
  const search = useMetaAdSearch(searching ? query : '', open && searching);
  const results = searching ? (search.data ?? []) : [];
  const [imported, setImported] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const importAd = useImportMetaAd();

  const fill = async ({
    imported: result,
    imageDataUrls,
  }: {
    imported: MetaAdImport;
    imageDataUrls: { feed: string | null; vertical: string | null }[];
  }) => {
    const media = await Promise.all(
      result.creatives.map(async (c, i) => ({
        feed: await importedMedia(imageDataUrls[i]?.feed ?? null, c.video).catch(() => null),
        vertical: c.vertical ? await importedMedia(imageDataUrls[i]?.vertical ?? null, c.vertical.video).catch(() => null) : null,
      })),
    );
    const creatives: DraftCreative[] = [];
    result.creatives.forEach((c, i) => {
      creatives.push(
        newCreative(creatives, { name: c.name, media: media[i]?.feed ?? null, vertical: media[i]?.vertical ?? null, overrides: c.overrides }),
      );
    });
    onImport(result, creatives);
    const count = result.creatives.length;
    setImported(count > 1 ? `Imported ${count} ads from "${result.sourceName}".` : `Imported "${result.sourceName}".`);
    const unread = creatives.flatMap((c, i) => [
      ...(c.media ? [] : [`the image for ${c.name}`]),
      ...(result.creatives[i]?.vertical && !c.vertical ? [`the 9:16 image for ${c.name}`] : []),
    ]);
    if (unread.length > 0) {
      setImageError(`The copy was imported, but ${unread.join(', ')} couldn't be read. Add ${unread.length > 1 ? 'them' : 'it'} below.`);
    }
  };

  const run = (source: MetaImportSource) => {
    setImageError(null);
    importAd.mutate(source, { onSuccess: data => void fill(data) });
  };
  const runText = () => {
    if (picked && picked.name === text) return run({ kind: picked.kind, id: picked.id });
    if (text.trim()) run({ url: text.trim() });
  };

  return (
    <div className="flex flex-col gap-1.5">
      <form
        className="flex items-end gap-2"
        onSubmit={e => {
          e.preventDefault();
          runText();
        }}
      >
        <div className="max-w-xl flex-1">
          <ComboboxField
            label="Start from an existing Meta ad"
            placeholder="Search a campaign, ad set or ad by name, or paste a link or ID"
            items={results}
            itemToStringLabel={r => r.name}
            value={picked}
            onValueChange={r => {
              setPicked(r);
              if (r) run({ kind: r.kind, id: r.id });
            }}
            inputValue={text}
            onInputValueChange={setText}
            open={open && results.length > 0}
            onOpenChange={setOpen}
            by="id"
            loading={search.isFetching}
          >
            {results.map(r => (
              <ComboboxOption key={`${r.kind}-${r.id}`} value={r}>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{r.name}</span>
                  <span className="truncate text-xs text-content-tertiary">{resultDetail(r)}</span>
                </span>
              </ComboboxOption>
            ))}
          </ComboboxField>
        </div>
        <Button type="submit" variant="secondary" disabled={!text.trim() || importAd.isPending} loading={importAd.isPending}>
          Import
        </Button>
      </form>
      {imageError && (
        <Text size="xs" className="text-text-red">
          {imageError}
        </Text>
      )}
      {importAd.error ? (
        <Text size="xs" className="text-text-red">
          {importAd.error.message}
        </Text>
      ) : imported ? (
        <Text size="xs" variant="tertiary">
          {imported} Change anything below, or ask the agent to.
        </Text>
      ) : null}
    </div>
  );
}
